import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

let _ = t => t,
    _t;

import { KOALA } from 'HubStyleTokens/colors';
import { Fragment, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { sortProductLines } from 'pricing-pages-lib/utils/sortingUtils';
import { getHighlightsForProductLine, getProductLevelFromApiNameString, getProductLineFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import breakpoint from '../../constants/Breakpoints';
import { useBusinessObjectives } from '../../hooks/useBusinessObjectives';
import { useFeatureStore } from '../../hooks/useFeatureStore';
import { getAllFeaturesByAllProductLinesSelector } from '../../selectors/FeatureSelectors';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { highestProductLevelFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import CompareProFeatureTableStaticHeader from './CompareProFeatureTableStaticHeader';
import FeatureTableRow from './FeatureTableRow';
import SuiteFeatureTableStickyHeader from './suite-feature-table/SuiteFeatureTableStickyHeader';
const TableContainer = styled.div.withConfig({
  displayName: "CompareProFeatureTable__TableContainer",
  componentId: "xltbi2-0"
})(["width:100%;padding:16px 0 60px;table{width:100%;border-spacing:0;table-layout:fixed;}table thead tr th:nth-child(1),table tbody tr th:nth-child(1){width:50%;border:1px solid ", ";}"], KOALA);
const paddingLeft = css(["padding-left:42px;", ""], breakpoint.tablet(_t || (_t = _`
    padding-left: 16px;
    `)));
const rowHeaderStyles = css(["text-align:left;", ""], paddingLeft);
const Table = styled.table.withConfig({
  displayName: "CompareProFeatureTable__Table",
  componentId: "xltbi2-1"
})(["tbody tr{display:table;width:100%;table-layout:fixed;min-height:70px;}"]);
const TableContent = styled.tbody.withConfig({
  displayName: "CompareProFeatureTable__TableContent",
  componentId: "xltbi2-2"
})(["position:relative;overflow:hidden;display:block;max-height:100%;& tr td{padding:16px 0;text-align:center;border:1px solid ", ";}& tr th[scope='row']{", "}"], KOALA, rowHeaderStyles);

const CompareProFeatureTable = ({
  upgradePointApiName
}) => {
  const upgradePointProductLine = getProductLineFromApiNameString(upgradePointApiName);
  const upgradePointProductLevel = getProductLevelFromApiNameString(upgradePointApiName);
  const highlightedProducts = getHighlightsForProductLine(upgradePointProductLine); // // determine highest owned level of productLine

  const productLevelOwned = useSelector(state => {
    const hasPricingAndPurchasingSucceeded = hasPricingAndPurchasingSucceededSelector(state);

    if (hasPricingAndPurchasingSucceeded) {
      return highestProductLevelFromProductInventorySelector(state, {
        productLine: upgradePointProductLine
      });
    }

    return null;
  });
  const filteredHighlightedProducts = highlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return [productLevelOwned, upgradePointProductLevel].includes(localProductLevel);
  });
  const productsByProductLine = {
    [upgradePointProductLine]: filteredHighlightedProducts
  };
  useBusinessObjectives({
    highlightedProducts: filteredHighlightedProducts
  });
  useFeatureStore({
    highlightedProducts: filteredHighlightedProducts,
    productLine: upgradePointProductLine
  });
  const features = useSelector(getAllFeaturesByAllProductLinesSelector);
  const featureTableRef = useRef(null);
  if (!Object.keys(features).length) return null;
  const sortedProductLines = sortProductLines(Object.keys(features));
  const levels = new Set();
  Object.values(productsByProductLine).forEach(products => {
    products.forEach(product => {
      const level = getProductLevelFromApiNameString(product);
      levels.add(level);
    });
  });
  const levelsAndApiNames = Object.entries(productsByProductLine).flatMap(([product, apiNames]) => apiNames ? apiNames.map(apiName => ({
    productLine: product,
    level: getProductLevelFromApiNameString(apiName)
  })) : []);
  return /*#__PURE__*/_jsxs(TableContainer, {
    ref: featureTableRef,
    columns: levels.size,
    "data-selenium": "suite-feature-table",
    id: "feature-table",
    children: [/*#__PURE__*/_jsx(SuiteFeatureTableStickyHeader, {
      levels: [...levels],
      tableRef: featureTableRef,
      isComparePro: true,
      levelsAndApiNames: levelsAndApiNames,
      upgradePointProductLevel: upgradePointProductLevel,
      upgradePointApiName: upgradePointApiName
    }), /*#__PURE__*/_jsx(Table, {
      children: sortedProductLines.map(sortedProductLine => {
        const localFeatures = features[sortedProductLine];
        const localHighlightedProducts = productsByProductLine[sortedProductLine];
        if (localHighlightedProducts === undefined || localHighlightedProducts.length <= 0) return null;
        return /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(CompareProFeatureTableStaticHeader, {
            levelsAndApiNames: levelsAndApiNames,
            upgradePointProductLevel: upgradePointProductLevel,
            upgradePointApiName: upgradePointApiName
          }), /*#__PURE__*/_jsx(TableContent, {
            id: `suite-feature-table-${sortedProductLine}-section`,
            children: localFeatures.map(feature => {
              return /*#__PURE__*/_jsx(FeatureTableRow, {
                highlightedProducts: localHighlightedProducts,
                feature: feature,
                productLine: sortedProductLine
              }, feature.featureId);
            })
          })]
        }, sortedProductLine);
      })
    })]
  });
};

export default CompareProFeatureTable;